import { Field } from "@/components/molecules/field";
import { SettingsSection } from "@/components/molecules/settings-section";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useMyClients,
  useUpdateClient,
} from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type {
  CallRecordingLevel,
  ClientCallRecording,
} from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import { useMemo } from "react";
import { Trans } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

type Scope = keyof ClientCallRecording;

/** Mirrors the backend's `callRecordingPolicy`: unset follows these. */
const DEFAULTS: Record<Scope, CallRecordingLevel> = {
  incoming: "audio",
  transfers: "audio",
  outgoing: "none",
};

const SCOPES: Scope[] = ["incoming", "transfers", "outgoing"];

const LEVELS: CallRecordingLevel[] = ["none", "text", "audio"];

/** A boolean is a choice saved before the levels existed. */
const toLevel = (
  value: ClientCallRecording[Scope],
  fallback: CallRecordingLevel,
): CallRecordingLevel => {
  if (value === true) return "audio";
  if (value === false) return "none";
  return value ?? fallback;
};

/** Where the GDPR page lives on the public site, in the reader's language. */
const privacyUrl = (language?: string) =>
  `https://heyup.ai/${language?.startsWith("fr") ? "fr" : "en"}/legal/privacy`;

/**
 * Which of the team's own phone conversations are recorded, transcribed and
 * summarised — and whether their audio is kept or only their transcript. The
 * company's call, not ours: it is the one that informs its callers and its
 * staff — so each choice saves on its own, and only an owner may make it.
 */
export function CallRecordingSection() {
  const { t, i18n } = useTranslation("companySettings");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: clients = [] } = useMyClients();
  const updateClient = useUpdateClient();
  const isOwner = useHasRole("OWNER");

  const settings = useMemo(
    () => clients.find((c) => c.client.id === clientId)?.client.call_recording,
    [clients, clientId],
  );

  const value = (scope: Scope) => toLevel(settings?.[scope], DEFAULTS[scope]);

  const save = async (scope: Scope, level: CallRecordingLevel) => {
    if (!clientId) return;
    try {
      await updateClient.mutateAsync({
        clientId,
        data: { call_recording: { ...settings, [scope]: level } },
      });
      toast.success(t("companySettings.messages.updated"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("companySettings.messages.updateError"),
      );
    }
  };

  return (
    <SettingsSection
      title={t("companySettings.callRecording.title")}
      description={t("companySettings.callRecording.description")}
    >
      {SCOPES.map((scope) => (
        <Field
          key={scope}
          orientation="horizontal"
          label={t(`companySettings.callRecording.${scope}.label`)}
          hint={t(`companySettings.callRecording.${scope}.hint`)}
          htmlFor={`call-recording-${scope}`}
        >
          <Select
            value={value(scope)}
            disabled={!isOwner || updateClient.isPending}
            onValueChange={(level) =>
              void save(scope, level as CallRecordingLevel)
            }
          >
            <SelectTrigger id={`call-recording-${scope}`} className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEVELS.map((level) => (
                <SelectItem key={level} value={level}>
                  {t(`companySettings.callRecording.levels.${level}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ))}
      <p className="text-sm text-muted-foreground">
        {t("companySettings.callRecording.levelsHint")}
      </p>
      <p className="text-sm text-muted-foreground">
        {t("companySettings.callRecording.notice")}
      </p>
      <p className="text-sm text-muted-foreground">
        <Trans
          i18nKey="companySettings.callRecording.hosting"
          components={{
            link: (
              <a
                href={privacyUrl(i18n.language)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
              />
            ),
          }}
        />
      </p>
    </SettingsSection>
  );
}
